import React from 'react';
import PropTypes from 'prop-types';

import alertIcon from 'core/assets/svg/alert-white-filled.svg';
import Divider from 'core/components/divider';
import { Label } from 'core/components/label';
import { Radio } from 'core/components/radio';
import { Select } from 'core/components/select';
import { nthDayFormatter, numberFormatter } from 'core/helpers/formatters';
import { InlineCallout } from 'quote-revisit/components/inline-callout';
import { LAST_BILLING_DAY } from './billing-day';
import { CloseResolution, SpacingBucket } from './payment-spacing';
import errorIcon from './ic-error-triangle-16px.svg';
import useStyles from './billing-day-controls.styles';

export const BillingDayChoice = Object.freeze({
  EffectiveDate: 'effective-date',
  Custom: 'custom'
});

const billingDayOptions = Array.from({ length: LAST_BILLING_DAY }, (_, index) => ({
  id: index + 1,
  value: String(index + 1)
}));

const days = (count) => (count === 1 ? '1 day' : `${count} days`);
const payments = (count) => (count === 1 ? '1 payment' : `${count} payments`);

const OptionLabel = ({ label, hint }) => {
  const classes = useStyles();
  return (
    <span className={classes.optionLabel}>
      <span>{label}</span>
      <span className={classes.hint}>{hint}</span>
    </span>
  );
};

OptionLabel.propTypes = {
  label: PropTypes.string.isRequired,
  hint: PropTypes.string.isRequired
};

// Under 15 days apart: the agent has to keep the payments separate or combine them (#36361).
const CloseResolutionChoice = ({ daysApart, paymentCount, termTotal, resolution, onResolutionChange, showError }) => {
  const classes = useStyles();
  return (
    <div className={classes.closePayments}>
      <img src={alertIcon} alt="" className={classes.alertIcon} />
      <Label type="body2" component="p" id="close-payments-question" className={classes.question}>
        {/* Label is a flex container, so the sentence needs its own inline box to keep its spaces. */}
        <span>
          The first two payments will land <strong>{`just ${days(daysApart)} apart`}</strong>. How do you want to handle
          it?
        </span>
      </Label>
      <Radio
        id="close-payments-resolution"
        name="closePaymentsResolution"
        aria-labelledby="close-payments-question"
        value={resolution ?? ''}
        onChange={(event) => onResolutionChange(event.target.value)}
        error={showError}
        className={classes.radio}
        labelClassName={classes.radioLabel}
        optionsSideBySide={false}
        options={[
          {
            value: CloseResolution.KeepSeparate,
            label: (
              <OptionLabel
                label="Keep payments separate"
                hint={`Keep both charges ${days(daysApart)} apart, ${payments(paymentCount)} total.`}
              />
            )
          },
          {
            value: CloseResolution.Combine,
            label: (
              <OptionLabel
                label="Combine into a single payment"
                hint={`One larger charge up front, ${payments(paymentCount - 1)} total.`}
              />
            )
          }
        ]}
      />
      {showError && (
        <Label type="body2" component="p" role="alert" className={classes.error}>
          <img src={errorIcon} alt="" className={classes.errorIcon} />
          Please choose an option to continue
        </Label>
      )}
      {termTotal != null && (
        <>
          <Divider className={classes.divider} />
          <InlineCallout variant="light" className={classes.termTotalNote}>
            {`The $${numberFormatter(termTotal, 2)} total term price doesn’t change — only the dates move.`}
          </InlineCallout>
        </>
      )}
    </div>
  );
};

CloseResolutionChoice.propTypes = {
  daysApart: PropTypes.number.isRequired,
  paymentCount: PropTypes.number.isRequired,
  termTotal: PropTypes.number,
  resolution: PropTypes.oneOf(Object.values(CloseResolution)),
  onResolutionChange: PropTypes.func.isRequired,
  showError: PropTypes.bool.isRequired
};

CloseResolutionChoice.defaultProps = {
  termTotal: null,
  resolution: null
};

/**
 * The payment schedule modal's billing day controls (#36361): follow the effective date, or pick a day
 * from the 1st to the 28th. A picked day says how far apart it puts the first two payments, from the
 * previewed schedule with the payments kept separate (`spacing`), and under 15 days asks the agent to keep
 * them separate or combine them.
 */
const BillingDayControls = ({
  effectiveDay,
  choice,
  onChoiceChange,
  customDay,
  onCustomDayChange,
  spacing,
  termTotal,
  resolution,
  onResolutionChange,
  showError
}) => {
  const classes = useStyles();
  return (
    <section className={classes.controls} aria-labelledby="billing-day-heading">
      <Label type="body1Bold" component="h3" id="billing-day-heading" className={classes.heading}>
        Billing day of the month
      </Label>
      <Radio
        id="billing-day-choice"
        name="billingDayChoice"
        aria-labelledby="billing-day-heading"
        value={choice}
        onChange={(event) => onChoiceChange(event.target.value)}
        className={classes.radio}
        labelClassName={classes.radioLabel}
        optionsSideBySide={false}
        options={[
          {
            value: BillingDayChoice.EffectiveDate,
            label: (
              <OptionLabel
                label="Use the effective date"
                hint={`Bills on this policy’s effective day of the month — currently the ${nthDayFormatter(effectiveDay)}.`}
              />
            )
          },
          {
            value: BillingDayChoice.Custom,
            label: (
              <OptionLabel
                label="Choose another date"
                hint="Pick any day from the 1st to the 28th to bill on each month."
              />
            )
          }
        ]}
      />
      {choice === BillingDayChoice.Custom && (
        <div className={classes.custom}>
          <Select
            id="custom-billing-day"
            name="customBillingDay"
            label="Custom billing day of the month"
            mode="light"
            icon="caretOrange"
            options={billingDayOptions}
            value={customDay}
            onChange={(_, value) => onCustomDayChange(Number(value))}
            className={classes.select}
            fullWidth
          />
          {spacing?.bucket === SpacingBucket.From15To30 && (
            <InlineCallout variant="light">{`The first two payments will land ${days(spacing.days)} apart.`}</InlineCallout>
          )}
          {spacing?.bucket === SpacingBucket.Under15 && (
            <CloseResolutionChoice
              daysApart={spacing.days}
              paymentCount={spacing.paymentCount}
              termTotal={termTotal}
              resolution={resolution}
              onResolutionChange={onResolutionChange}
              showError={showError}
            />
          )}
        </div>
      )}
    </section>
  );
};

BillingDayControls.propTypes = {
  effectiveDay: PropTypes.number.isRequired,
  choice: PropTypes.oneOf(Object.values(BillingDayChoice)).isRequired,
  onChoiceChange: PropTypes.func.isRequired,
  customDay: PropTypes.number.isRequired,
  onCustomDayChange: PropTypes.func.isRequired,
  spacing: PropTypes.shape({
    days: PropTypes.number.isRequired,
    bucket: PropTypes.oneOf(Object.values(SpacingBucket)).isRequired,
    paymentCount: PropTypes.number.isRequired
  }),
  termTotal: PropTypes.number,
  resolution: PropTypes.oneOf(Object.values(CloseResolution)),
  onResolutionChange: PropTypes.func.isRequired,
  showError: PropTypes.bool.isRequired
};

BillingDayControls.defaultProps = {
  spacing: null,
  termTotal: null,
  resolution: null
};

export default BillingDayControls;
